import {extract} from '@danwang/extractor';
import {Option, none} from '@danwang/option';
import {
  FieldFilterMatch,
  NetAmountFilter,
  TransactionKind,
} from '@transactions/graphql';
import _ from 'lodash';
import {
  normalizeAmountBounds,
  parseAmountParam,
} from 'src/components/AmountFilterSelect';
import {TRANSFER_KINDS, isTransactionKind} from 'src/transactionKinds';
import type {FieldFilterInput} from '@transactions/graphql';

const isFieldFilterMatch = (value: unknown): value is FieldFilterMatch =>
  value === FieldFilterMatch.Exact ||
  value === FieldFilterMatch.Contains ||
  value === FieldFilterMatch.Regex ||
  value === FieldFilterMatch.Unset;

/**
 * URLs and saved filters from before the tiered Type filter carried an
 * exclusive netAmountFilter, plus transferTypes under BALANCED. Map them onto
 * the kinds they selected, so old links and revived localStorage keep
 * narrowing the same rows.
 */
const legacyKinds = (
  netAmountFilter: unknown,
  transferTypes: readonly string[] | null | undefined,
): TransactionKind[] | null => {
  if (netAmountFilter === NetAmountFilter.Spending) {
    return [TransactionKind.Spending];
  }
  if (netAmountFilter === NetAmountFilter.Income) {
    return [TransactionKind.Income];
  }
  if (netAmountFilter === NetAmountFilter.Balanced) {
    const subtypes = transferTypes?.filter(isTransactionKind) ?? [];
    // BALANCED alone meant every transfer, which the tiered filter spells as
    // the whole Transfers group.
    return subtypes.length > 0 ? subtypes : [...TRANSFER_KINDS];
  }
  return null;
};

const extractor = extract.shape({
  categories: extract.nullable(extract.array(extract.string)),
  tags: extract.nullable(extract.array(extract.string)),
  search: extract.nullable(extract.string),
  fieldFilters: extract.nullable(
    extract.array(
      extract.shape({
        fieldId: extract.string,
        match: extract.string,
        value: extract.nullable(extract.string),
      }),
    ),
  ),
  startDate: extract.nullable(extract.string),
  endDate: extract.nullable(extract.string),
  // nullish, not nullable: filters saved before these keys existed lack them
  // entirely, and a missing key must not throw away the rest of the filters.
  minAmount: extract.nullish(extract.number),
  maxAmount: extract.nullish(extract.number),
  kinds: extract.nullish(extract.array(extract.string)),
  // Legacy keys, no longer written: nullish so their absence from new saves
  // is fine, kept readable so old saves map through legacyKinds.
  netAmountFilter: extract.nullish(extract.string),
  transferTypes: extract.nullish(extract.array(extract.string)),
});

export const saveFiltersToLocalStorage = (
  filters: TransactionFilters,
  key: string,
) => {
  window.localStorage.setItem(key, JSON.stringify(filters));
};

export const getInitialFilters = (
  sp: URLSearchParams,
  key: string,
): TransactionFilters => {
  if (sp.toString() === '') {
    return TransactionFilters.fromLocalStorage(key).getOrElse(TransactionFilters.empty);
  } else {
    return TransactionFilters.fromSearchParams(sp);
  }
};

type Filters = {
  categories: string[] | null;
  tags: string[] | null;
  search: string | null;
  fieldFilters: FieldFilterInput[] | null;
  /**
   * Tiered type filter: the union of the selected payment classes, transfer
   * subtypes, and untyped transfers. Null (or empty) means no type filter.
   */
  kinds: TransactionKind[] | null;
  startDate: string | null;
  endDate: string | null;
  /** Minimum magnitude (absolute value) of the amount, in dollars. */
  minAmount: number | null;
  /** Maximum magnitude (absolute value) of the amount, in dollars. */
  maxAmount: number | null;
};

const empty: Filters = {
  categories: null,
  tags: null,
  search: null,
  fieldFilters: null,
  kinds: null,
  startDate: null,
  endDate: null,
  minAmount: null,
  maxAmount: null,
};

class TransactionFilters {
  readonly categories: string[] | null;
  readonly tags: string[] | null;
  readonly search: string | null;
  readonly fieldFilters: FieldFilterInput[] | null;
  readonly kinds: TransactionKind[] | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly minAmount: number | null;
  readonly maxAmount: number | null;

  constructor(filters: Filters) {
    this.categories =
      filters.categories && filters.categories.length > 0
        ? filters.categories
        : null;
    this.tags = filters.tags && filters.tags.length > 0 ? filters.tags : null;
    this.search = filters.search || null;
    this.fieldFilters =
      filters.fieldFilters && filters.fieldFilters.length > 0
        ? filters.fieldFilters
        : null;
    this.kinds =
      filters.kinds && filters.kinds.length > 0 ? _.uniq(filters.kinds) : null;
    this.startDate = filters.startDate || null;
    this.endDate = filters.endDate || null;
    // The server rejects negative or backwards magnitude bounds, and these
    // can arrive hand-typed in the URL or revived from older saved filters:
    // normalize (clamp to whole positive cents, swap a backwards pair)
    // before anything reads them.
    const bounds = normalizeAmountBounds({
      minAmount: filters.minAmount,
      maxAmount: filters.maxAmount,
    });
    this.minAmount = bounds.minAmount;
    this.maxAmount = bounds.maxAmount;
  }

  static readonly empty: TransactionFilters = new TransactionFilters(empty);

  merge = (overrides: Partial<Filters>) => {
    return new TransactionFilters({
      categories: this.categories,
      tags: this.tags,
      search: this.search,
      fieldFilters: this.fieldFilters,
      kinds: this.kinds,
      startDate: this.startDate,
      endDate: this.endDate,
      minAmount: this.minAmount,
      maxAmount: this.maxAmount,
      ...overrides,
    });
  };

  static create = (values: Partial<Filters>) =>
    TransactionFilters.empty.merge(values);

  toQueryString = (): string => {
    const {categories, tags, search, kinds, startDate, endDate, minAmount, maxAmount} = this;
    // Through URLSearchParams so values are escaped: a search for "H&M"
    // must come back as "H&M", not as "H" plus a stray "M" parameter.
    // Commas stay readable — they join lists and are not a delimiter here.
    const params = new URLSearchParams();
    if (categories) params.set('categories', categories.join(','));
    if (tags) params.set('tags', tags.join(','));
    if (search) params.set('search', search);
    if (kinds) params.set('kinds', kinds.join(','));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (minAmount != null) params.set('minAmount', String(minAmount));
    if (maxAmount != null) params.set('maxAmount', String(maxAmount));
    return params.toString().replace(/%2C/g, ',');
  };

  equals = (other: TransactionFilters): boolean => {
    return (
      this.categories?.sort().join(',') ===
        other.categories?.sort().join(',') &&
      this.tags?.sort().join(',') === other.tags?.sort().join(',') &&
      this.search === other.search &&
      JSON.stringify(this.fieldFilters) ===
        JSON.stringify(other.fieldFilters) &&
      this.kinds?.sort().join(',') === other.kinds?.sort().join(',') &&
      this.startDate === other.startDate &&
      this.endDate === other.endDate &&
      this.minAmount === other.minAmount &&
      this.maxAmount === other.maxAmount
    );
  };

  static fromSearchParams = (sp: URLSearchParams): TransactionFilters => {
    // Hand-typed URLs can hold anything; unknown tokens are dropped here
    // rather than forwarded for the server to reject the whole query over.
    const kindsParam = sp.get('kinds');
    const kinds =
      kindsParam != null
        ? kindsParam.split(',').filter(isTransactionKind)
        : legacyKinds(
            sp.get('netAmountFilter'),
            sp.get('transferTypes')?.split(','),
          );

    return TransactionFilters.create({
      categories: Option(sp.get('categories'))
        .map((categories) => categories.split(','))
        .getOrElse(null),
      tags: Option(sp.get('tags'))
        .map((tags) => tags.split(','))
        .getOrElse(null),
      search: sp.get('search'),
      kinds,
      startDate: sp.get('startDate'),
      endDate: sp.get('endDate'),
      minAmount: parseAmountParam(sp.get('minAmount')),
      maxAmount: parseAmountParam(sp.get('maxAmount')),
    });
  };

  static fromLocalStorage = (key: string): Option<TransactionFilters> => {
    const savedFilters = window.localStorage.getItem(key);
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      return extractor(parsed)
        .toOption()
        .map((extracted) => ({
          categories: extracted.categories,
          tags: extracted.tags,
          search: extracted.search,
          fieldFilters: extracted.fieldFilters?.flatMap(
            (f): FieldFilterInput[] => {
              if (!isFieldFilterMatch(f.match)) return [];
              return [{fieldId: f.fieldId, match: f.match, value: f.value}];
            },
          ) ?? null,
          kinds:
            extracted.kinds != null
              ? extracted.kinds.filter(isTransactionKind)
              : legacyKinds(extracted.netAmountFilter, extracted.transferTypes),
          startDate: extracted.startDate ?? null,
          endDate: extracted.endDate ?? null,
          minAmount: extracted.minAmount ?? null,
          maxAmount: extracted.maxAmount ?? null,
        }))
        .map(TransactionFilters.create);
    } else {
      return none;
    }
  };
}

export default TransactionFilters;
